import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ChevronRight, Stethoscope, Pill, Flower2, Crown, Droplets } from "lucide-react";
import api from "@/lib/api";

const KIND_ICON = {
  inspection: Stethoscope,
  treatment: Pill,
  seasonal: Flower2,
  queen: Crown,
  harvest: Droplets,
};

const PRIORITY_DOT = {
  high: "bg-rose-500",
  medium: "bg-amber-500",
  low: "bg-stone-400",
};

export default function SmartCalendarWidget() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/predictions/dashboard", { params: { limit: 3 } });
        if (!cancelled) setItems(r.data);
      } catch (e) {
        if (e.response?.status === 402 && !cancelled) setGated(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (gated) {
    return (
      <Link
        to="/app/settings?tab=billing"
        className="block border border-amber-200 bg-amber-50/60 rounded-2xl p-5 hover:border-amber-300 transition"
        data-testid="smart-calendar-widget-upsell"
      >
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-amber-600" />
          <span className="text-xs uppercase tracking-[0.18em] font-bold text-amber-800">Pro · Smart calendar</span>
        </div>
        <p className="text-sm text-stone-700">Get tailored treatment, inspection and seasonal nudges. Upgrade to unlock.</p>
      </Link>
    );
  }
  if (items.length === 0) return null;

  return (
    <section className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="smart-calendar-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-600" />
          <h3 className="font-display text-lg font-medium">Smart calendar</h3>
        </div>
        <Link
          to="/app/smart-calendar"
          className="text-xs text-stone-600 hover:text-stone-900 font-medium inline-flex items-center gap-0.5"
          data-testid="smart-calendar-widget-link"
        >
          See all <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      <ul className="space-y-2">
        {items.map((p) => {
          const Icon = KIND_ICON[p.kind] || Stethoscope;
          return (
            <li
              key={p.id}
              className="flex items-start gap-3 py-2 border-b border-stone-100 last:border-0"
              data-testid={`smart-calendar-widget-item-${p.id}`}
            >
              <div className="flex items-center gap-2 shrink-0 pt-0.5">
                <span className={`w-1.5 h-1.5 rounded-full ${PRIORITY_DOT[p.priority] || PRIORITY_DOT.low}`} />
                <Icon className="w-4 h-4 text-stone-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-stone-900 truncate">{p.title}</div>
                <div className="text-xs text-stone-500 truncate">{p.reason}</div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
